<script setup>

import DetailEditorLayout from "../../components/view-components/DetailEditorLayout.vue";
import FormTheming from "../../components/ui-components/FormTheming.vue";
import {computed, ref} from "vue";
import { trans } from 'laravel-vue-i18n';
import {router, usePage} from "@inertiajs/vue3";
import TimeSlotComponent from "../../components/ui-components/TimeSlotComponent.vue";
import FormSettings from "../../components/ui-components/FormSettings.vue";
import _ from "lodash";
import ServicesEdit from "../../components/ui-components/ServicesEdit.vue";
import RentalSubjects from "../../wizards/registrationForm/rental/RentalSubjects.vue";

const pageprops = usePage().props

const theme = ref({
    color: pageprops.theme.color,
    borderless: pageprops.theme.borderless,
    transparent: false,
    darkMode: pageprops.theme.mode_lock === 'dark',
    darkModeLock: pageprops.theme.mode_lock !== 'auto',
    background_color: pageprops.theme.background_color,
    background_color_dark: pageprops.theme.background_color_dark,
    background_image_url: pageprops.theme.background_image_url,
    background_image_url_dark: pageprops.theme.background_image_url_dark,
    logo_url: pageprops.theme.logo_url,
    logo_url_dark: pageprops.theme.logo_url_dark,
    panel_color: pageprops.theme.panel_color,
    panel_color_dark: pageprops.theme.panel_color_dark,
    panel_transparency: pageprops.theme.panel_transparency,
    panel_transparency_dark: pageprops.theme.panel_transparency_dark,
    panel_backdrop_blur: pageprops.theme.panel_backdrop_blur,
    panel_backdrop_blur_dark: pageprops.theme.panel_backdrop_blur_dark,
    fontFamily: pageprops.theme.fontFamily,
    rows: pageprops.theme.rows,
    columns: pageprops.theme.columns,
    roundness: pageprops.theme.roundness,
})

const settings = ref({
    firstDayOfWeek: pageprops.settings.firstDayOfWeek,
    titlePosition: pageprops.settings.titlePosition,
    transition: pageprops.settings.transition,
    locale: pageprops.settings.locale,
    showWeekNumbers: pageprops.settings.showWeekNumbers,
    view: pageprops.settings.view,
})

const form = ref({
    name: pageprops.forms?.name,
    language: pageprops.forms?.locale,
    description: pageprops.forms?.description,
    description_code: pageprops.forms?.description_code,
    reservation_change_settings: pageprops.forms?.reservation_change_settings,
    reservation_cancel_settings: pageprops.forms?.reservation_cancel_settings,
    reservation_change_auto_approve: pageprops.forms?.reservation_change_auto_approve,
    reservation_cancel_auto_approve: pageprops.forms?.reservation_cancel_auto_approve,
    notifications: {
        newClient: true,
        confirmClient: true,
        changeClient: true,
        cancelClient: true,
        reminderClient: false,
        retargetingClient: false,
    }
})

const timeSlots = ref(Object.values(pageprops.valid_days))

const services = ref(pageprops.services)

const multiDayUnits = ['day', 'weekend', 'week', 'half_month', 'month', 'quarter', 'year']

const rentalModel = ref({
    reservationType: (() => {
        if (pageprops.forms?.form_type?.name === 'accommodation') {
            return 'multi-day';
        }
        const firstUnit = pageprops.rental_items?.[0]?.unit;
        return firstUnit && multiDayUnits.includes(firstUnit) ? 'multi-day' : 'single-day';
    })(),
    rentalItems: (pageprops.rental_items ?? []).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        icon: item.type,
        type: item.type,
        calendar_id: item.external_calendar?.calendar_api_key ?? null,
        unit: item.unit,
        min_unit_quantity: item.min_unit_quantity,
        payment_style: item.payment_style,
        unit_price: item.prices?.[0]?.unit_price ?? 0,
        max_pop: item.max_pop,
        vat_percentage: item.prices?.[0]?.vat_percentage ?? 0,
        currency: item.prices?.[0]?.currency ?? 'CZK',
        attributes: item.attributes ?? [],
        discounts: item.discounts ?? [],
        additional_costs: item.additional_costs ?? [],
        occupancy_rules: item.pricing_rules ?? [],
        pop_rules: item.pop_rules ?? [],
        booking_rules: item.booking_rules ?? [],
        timeSlots: item.valid_days ?? [],
        gallery: item.gallery ?? [],
    })),
})

const isRentalType = computed(() =>
    ['rental', 'accommodation'].includes(pageprops.forms?.form_type?.name)
)

const currentSection = ref('info')

const sections = computed(() => [
    { id: 'info',      icon: 'pi pi-check-square', label: trans('forms.wizard_tab_info') },
    { id: 'theme',     icon: 'pi pi-palette',      label: trans('forms.wizard_tab_theme') },
    { id: 'timeslots', icon: 'pi pi-clock',        label: trans('forms.wizard_tab_timeslots') },
    ...(pageprops.forms?.form_type?.name === 'service'
        ? [{ id: 'services', icon: 'pi pi-briefcase', label: trans('forms.wizard_tab_services') }]
        : []),
    ...(isRentalType.value
        ? [{
            id: 'rental',
            icon: 'pi pi-box',
            label: pageprops.forms?.form_type?.name === 'accommodation' ? trans('forms.wizard_tab_accommodation') : trans('forms.wizard_tab_rental'),
          }]
        : []),
])

const send = (goBack = false) => {
    router.post(usePage().url, {
        theme: theme.value,
        settings: settings.value,
        form: form.value,
        timeSlots: timeSlots.value,
        services: services.value,
        rentalItems: isRentalType.value ? rentalModel.value.rentalItems : undefined,
        goBack: goBack,
    })
}
</script>

<template>
    <detail-editor-layout :title="$t('forms.update_form_title')" @on-save="send" @on-save-back="send(true)" borderless>
        <div class="border dark:border-neutral-700 overflow-hidden bg-white dark:bg-neutral-900 rounded-xl flex min-h-[600px]">

            <!-- Sidebar -->
            <div class="w-[220px] shrink-0 border-r dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950 p-3 flex flex-col gap-0.5">
                <button
                    v-for="section in sections"
                    :key="section.id"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-left w-full group"
                    :class="currentSection === section.id
                        ? 'bg-white dark:bg-neutral-800 font-semibold shadow-sm text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-white/70 dark:hover:bg-neutral-800/70 hover:text-gray-900 dark:hover:text-white'"
                    @click="currentSection = section.id"
                >
                    <i :class="[section.icon, currentSection === section.id ? 'text-roori-blue' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300']"
                       class="w-4 shrink-0 text-center text-sm"></i>
                    <span>{{ section.label }}</span>
                </button>
            </div>

            <!-- Content -->
            <div class="grow overflow-auto p-6">

                <form-settings v-if="currentSection === 'info'" v-model="form" only-basics/>

                <form-theming v-else-if="currentSection === 'theme'"
                              v-model:theme="theme" v-model:settings="settings" :forms="form"/>

                <time-slot-component
                    v-else-if="currentSection === 'timeslots'"
                    v-model="timeSlots"
                    :worker="pageprops.services.filter(el => el.workers.length > 0).length > 0"
                    :workers="_.uniqBy(pageprops.services.map(el => el.workers).flat().map(el => ({
                        name: el.name,
                        id: el.id,
                        initials: el.initials
                    })), 'id')"
                />

                <services-edit
                    v-else-if="currentSection === 'services'"
                    v-model="services"
                />

                <rental-subjects
                    v-else-if="currentSection === 'rental'"
                    v-model="rentalModel"
                    :form-type="$page.props.forms.form_type.name"
                />

            </div>
        </div>
    </detail-editor-layout>
</template>

<style scoped lang="scss">

</style>
